// src/services/publicationsServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/publications`;

/* Helpers */
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      const joined = v.map((x) => String(x).trim()).filter(Boolean).join(",");
      if (joined) usp.append(k, joined);
    } else if (typeof v === "boolean") {
      usp.append(k, v ? "true" : "false");
    } else {
      usp.append(k, String(v));
    }
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

/* ========= Public ========= */
export const getPublications = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllPublications`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des publications :", error);
    return [];
  }
};

export const getLastThreePublications = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getLastThreePublications`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des dernières publications :", error);
    return [];
  }
};

export const getPublicationById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur chargement publication :", error);
    return null;
  }
};

export const toAbsolutePublicationImageUrl = (url) => {
  if (!url) return "";
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/") || url.startsWith("uploads/")) {
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  }
  return url;
};

/* ========= Admin ========= */
// params: { q, is_active, page=1, limit=24, sort='created_at'|'titre', dir='ASC'|'DESC' }
export const searchPublications = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (error) {
    console.error("Erreur recherche publications :", error);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

// payload: { titre*, description?, image_publication_url?, lien_publication_url?, is_active? }
export const createPublication = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur création publication :", error);
    return null;
  }
};

export const updatePublication = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur mise à jour publication :", error);
    return null;
  }
};

export const patchPublication = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur patch publication :", error);
    return null;
  }
};

export const togglePublicationActive = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}/toggle`, null, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur toggle publication :", error);
    return null;
  }
};

export const deletePublication = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return true;
  } catch (error) {
    console.error("Erreur suppression publication :", error);
    return false;
  }
};
